import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { GridIcon, MoreHorizontalIcon, PlusIcon, XCircleIcon } from 'lucide-react';

import { ApiError, api, type CellCoverage, type MatrixCellRow, type PersonaRow } from '@/lib/api';
import { useContent, useT, useTn } from '@/lib/i18n';
import { useCannotBecause } from '@/lib/permissions';
import { notify } from '@/lib/notify';
import { InfoHint } from '@/components/info-hint';
import { PageHeader } from '@/components/page-header';
import { EmptyState } from '@/components/empty-state';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { CreatePersonaDialog, PersonaEditor } from '@/features/personas/editor';

/**
 * Personas and the persona × module matrix (build/09 §6, `11` Flow 5–6; `plan-beta` C).
 *
 * TWO THINGS ON ONE SCREEN, because they are two views of one decision. The
 * persona's plan template says WHICH modules an audience sits and in what order;
 * the matrix cell says HOW MANY items each of those modules serves it. Splitting
 * them would mean editing a plan without being able to see whether the bank can
 * fill it, which is the failure the coverage read exists to prevent.
 *
 * A grid rather than a `<DataTable>` on purpose: this is a two-dimensional
 * relationship, and the thing an author needs to see is which INTERSECTIONS are
 * amber. A flat list of 9 (or 60) cells hides exactly that.
 *
 * COVERAGE COMES FROM THE SERVER, in one aggregation for the whole grid —
 * never a request per cell. Amber means the certified bank cannot currently
 * satisfy that cell, which is the signal that stops a persona being published
 * into a configuration that fails at generation time.
 *
 * WHAT T4 ADDED. Personas had no write routes at all — six seeded rows and no
 * way to add a seventh, rename one, or change what any of them sat. The cards
 * below are the persona editor's entry point, and the one rule they make visible
 * is that a plan edit is a *draft*: candidates keep sitting the published plan
 * until someone publishes the draft, which mints a version rather than
 * overwriting the one every generated run is pinned to.
 */

function coverageKey(personaId: string, moduleId: string) {
  return `${personaId}::${moduleId}`;
}

function CellEditor({
  cell,
  coverage,
  onClose,
}: {
  cell: MatrixCellRow;
  coverage?: CellCoverage;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const t = useT();
  /**
   * R3. A matrix cell is `items:author` — it decides which certified items are
   * sampled into a run, which is item-bank work rather than a governance act.
   * Publishing the plan that USES the cell is separate, and admin-only.
   */
  const cannotAuthor = useCannotBecause('items:author');
  const [itemCount, setItemCount] = useState(cell.itemCount);
  const [shuffle, setShuffle] = useState(cell.shuffle);
  const [byDimension, setByDimension] = useState(cell.balancing.byDimension);
  const [live, setLive] = useState<CellCoverage | undefined>(coverage);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      api.saveMatrixCell({
        ...cell,
        itemCount,
        shuffle,
        balancing: { byDimension },
      }),
    onSuccess: ({ coverage: fresh }) => {
      // The save returns this cell's coverage, so amber appears immediately
      // rather than after refetching the whole grid to find out.
      setLive(fresh);
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['matrix'] });
      void queryClient.invalidateQueries({ queryKey: ['coverage'] });
      notify.success(
        t('matrix.cellSaved'),
        t(fresh.status === 'ok' ? 'matrix.cellSaved.ok' : 'matrix.cellSaved.amber', {
          available: fresh.effectivePoolSize,
          requested: fresh.itemCount,
        }),
      );
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : t('matrix.cellSaveFailed')),
  });

  const short = live && live.effectivePoolSize < itemCount;

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>
          {cell.personaId} · {cell.moduleId}
        </DialogTitle>
        <DialogDescription>{t('matrix.cell.description')}</DialogDescription>
      </DialogHeader>

      <div className="grid gap-4">
        <div className="grid gap-2">
          <Label htmlFor="itemCount">
            {t('matrix.itemCount')}
            <InfoHint>{t('matrix.itemCount.detail')}</InfoHint>
          </Label>
          <Input
            id="itemCount"
            type="number"
            min={1}
            max={60}
            value={itemCount}
            onChange={(e) => setItemCount(Number(e.target.value))}
          />
        </div>

        <div className="flex items-center gap-2">
          <input id="shuffle" type="checkbox" checked={shuffle} onChange={(e) => setShuffle(e.target.checked)} />
          <Label htmlFor="shuffle" className="font-normal">{t('matrix.shuffle')}</Label>
        </div>

        <div className="flex items-center gap-2">
          <input
            id="byDimension"
            type="checkbox"
            checked={byDimension}
            onChange={(e) => setByDimension(e.target.checked)}
          />
          <Label htmlFor="byDimension" className="font-normal">{t('matrix.balance')}</Label>
        </div>

        {live && (
          <Alert variant={live.status === 'warning' ? 'destructive' : 'default'}>
            <AlertDescription>
              <p>
                {t('matrix.poolSummary', {
                  available: live.effectivePoolSize,
                  requested: itemCount,
                })}
              </p>
              {short && <p className="mt-1">{t('matrix.willRefuse')}</p>}
              {live.dimensionBalance.length > 0 && (
                <ul className="mt-2 space-y-0.5 text-xs">
                  {live.dimensionBalance.map((d) => (
                    <li key={d.dimension} className={d.ok ? '' : 'font-medium'}>
                      {t('matrix.dimensionBalance', {
                        dimension: d.dimension,
                        available: d.available,
                        needed: d.needed,
                      })}{' '}
                      {d.ok ? '✓' : '⚠'}
                    </li>
                  ))}
                </ul>
              )}
            </AlertDescription>
          </Alert>
        )}

        {error && (
          <Alert variant="destructive" role="alert">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          {t('common.close')}
        </Button>
        <Button
          onClick={() => save.mutate()}
          disabled={save.isPending || cannotAuthor !== null}
          title={cannotAuthor ?? undefined}
        >
          {save.isPending ? t('common.saving') : t('matrix.saveCell')}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

/**
 * Retire and reactivate (T4).
 *
 * REVERSIBLE, where a module retire is terminal — a persona's state is a
 * boolean, and there is no third value for a terminal state to occupy. What it
 * is NOT is free: an open run means a candidate part-way through a multi-module
 * plan, and `generation.ts` refuses an inactive persona, so retiring now would
 * leave them unable to start the rest of it. The server refuses and says how
 * many; this menu surfaces that message verbatim rather than restating the rule
 * in shorter words that omit the way out.
 */
function PersonaLifecycleMenu({ persona }: { persona: PersonaRow }) {
  const queryClient = useQueryClient();
  const t = useT();
  const tn = useTn();

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['personas'] });
    void queryClient.invalidateQueries({ queryKey: ['persona', persona.personaId] });
  };

  const retire = useMutation({
    mutationFn: () => api.retirePersona(persona.personaId),
    onSuccess: ({ impact }) => {
      notify.success(
        t('personas.retired', { persona: persona.personaId }),
        tn('personas.retired.detail', impact.completedInstances),
      );
      refresh();
    },
    onError: (err) =>
      notify.error(t('modules.retireFailed'), err instanceof Error ? err.message : undefined),
  });

  const reactivate = useMutation({
    mutationFn: () => api.reactivatePersona(persona.personaId),
    onSuccess: (saved) => {
      notify.success(
        t('personas.reactivated', { persona: persona.personaId }),
        t('personas.reactivated.detail', { version: saved.planVersion }),
      );
      refresh();
    },
    onError: (err) =>
      notify.error(t('personas.reactivateFailed'), err instanceof Error ? err.message : undefined),
  });

  const busy = retire.isPending || reactivate.isPending;
  // R3. Retiring or reactivating a persona changes what a live candidate can be
  // generated from — `catalogue:publish`, admin only, like publishing its plan.
  const cannotPublish = useCannotBecause('catalogue:publish');

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          disabled={busy || cannotPublish !== null}
          title={cannotPublish ?? undefined}
          aria-label={t('modules.lifecycleActions', { module: persona.personaId })}
        >
          <MoreHorizontalIcon className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        <DropdownMenuLabel className="font-normal">
          <span className="block text-xs font-medium">
            {t('modules.currently', {
              status: t(persona.active ? 'personas.active' : 'personas.retiredState'),
            })}
          </span>
          <span className="text-muted-foreground block text-xs">
            {t(persona.active ? 'personas.activeHint' : 'personas.retiredHint')}
          </span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {persona.active ? (
          <DropdownMenuItem onSelect={() => retire.mutate()}>
            {t('personas.action.retire')}
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem onSelect={() => reactivate.mutate()}>
            {t('personas.action.reactivate')}
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function Matrix() {
  const t = useT();
  const tn = useTn();
  const content = useContent();
  const [editing, setEditing] = useState<MatrixCellRow | null>(null);
  const [editingPersona, setEditingPersona] = useState<string | null>(null);
  const [creatingPersona, setCreatingPersona] = useState(false);

  const personas = useQuery({ queryKey: ['personas'], queryFn: api.personas });
  const modules = useQuery({ queryKey: ['modules'], queryFn: api.modules });
  const cells = useQuery({ queryKey: ['matrix'], queryFn: api.matrix });
  const coverage = useQuery({ queryKey: ['coverage'], queryFn: api.coverage });

  const isLoading = personas.isLoading || modules.isLoading || cells.isLoading;
  const error = personas.error ?? modules.error ?? cells.error ?? coverage.error;

  const cellByKey = useMemo(
    () => new Map((cells.data?.items ?? []).map((c) => [coverageKey(c.personaId, c.moduleId), c])),
    [cells.data],
  );
  const coverageByKey = useMemo(
    () => new Map((coverage.data?.items ?? []).map((c) => [coverageKey(c.personaId, c.moduleId), c])),
    [coverage.data],
  );

  const personaRows = personas.data?.items ?? [];
  const moduleCols = modules.data?.items ?? [];
  const amber = (coverage.data?.items ?? []).filter((c) => c.status === 'warning').length;

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader
        title={t('matrix.title')}
        description={t('matrix.description')}
        actions={
          <div className="flex items-center gap-2">
            {coverage.data && (
              <Badge variant={amber ? 'destructive' : 'default'}>
                {amber ? tn('matrix.amber', amber) : t('matrix.allCovered')}
              </Badge>
            )}
            <Button size="sm" onClick={() => setCreatingPersona(true)}>
              <PlusIcon className="size-4" />
              {t('personas.new')}
            </Button>
          </div>
        }
      />

      {isLoading && <Skeleton className="h-64 w-full" />}

      {error && (
        <EmptyState
          icon={XCircleIcon}
          title={t('matrix.loadFailed')}
          description={error instanceof Error ? error.message : t('table.errorDescription')}
        />
      )}

      {!isLoading && !error && personaRows.length === 0 && (
        <EmptyState
          icon={GridIcon}
          title={t('personas.empty.title')}
          description={t('personas.empty.description')}
          actions={
            <Button onClick={() => setCreatingPersona(true)}>{t('personas.empty.action')}</Button>
          }
        />
      )}

      {!isLoading && !error && personaRows.length > 0 && (
        <>
          <div className="mb-6 grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {personaRows.map((p) => (
              <div key={p.personaId} className="rounded-md border p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate font-medium">{content(p.name)}</p>
                    <p className="text-muted-foreground truncate text-xs">{p.segment}</p>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    {!p.active && <Badge variant="outline">{t('personas.retiredState')}</Badge>}
                    <PersonaLifecycleMenu persona={p} />
                  </div>
                </div>

                <div className="mt-2 flex flex-wrap items-center gap-1">
                  <Badge variant="secondary" className="font-mono text-xs" dir="ltr">
                    {p.planVersion}
                  </Badge>
                  <Badge variant="outline">{tn('modules.count', p.moduleCount)}</Badge>
                  {/* The flag is the server's, and an EMPTY draft is still a
                      draft — a card that inferred this from `.length` would call
                      "this audience should sit nothing" a clean persona. */}
                  {p.hasDraft && <Badge variant="destructive">{t('personas.draftPending')}</Badge>}
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3 w-full"
                  onClick={() => setEditingPersona(p.personaId)}
                >
                  {t('personas.editPlan')}
                </Button>
              </div>
            ))}
          </div>

          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted/50">
                  <th className="p-3 text-start font-medium">{t('startRun.persona')}</th>
                  {moduleCols.map((m) => (
                    <th key={m.moduleId} className="p-3 text-start font-medium">
                      {content(m.name)}
                      <span className="text-muted-foreground block text-xs font-normal">
                        {t('questionTypes.certifiedCount', { count: m.certifiedCount })}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {personaRows.map((p) => (
                  <tr key={p.personaId} className="border-t">
                    <td className="p-3">
                      <p className="font-medium">{content(p.name)}</p>
                      <p className="text-muted-foreground text-xs">{p.segment}</p>
                    </td>
                    {moduleCols.map((m) => {
                      const key = coverageKey(p.personaId, m.moduleId);
                      const cell = cellByKey.get(key);
                      const cov = coverageByKey.get(key);

                      if (!cell) {
                        return (
                          <td key={key} className="text-muted-foreground p-3 text-xs">
                            {t('matrix.noCell')}
                          </td>
                        );
                      }

                      return (
                        <td key={key} className="p-2">
                          <button
                            type="button"
                            onClick={() => setEditing(cell)}
                            className="hover:bg-muted w-full rounded-md border p-2 text-start transition"
                          >
                            <span className="font-medium">
                              {tn('matrix.items', cell.itemCount)}
                            </span>
                            {cov && (
                              <Badge
                                variant={cov.status === 'warning' ? 'destructive' : 'secondary'}
                                className="ms-2"
                              >
                                {t('matrix.available', { count: cov.effectivePoolSize })}
                              </Badge>
                            )}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>


        </>
      )}

      <Dialog open={editing !== null} onOpenChange={(open) => !open && setEditing(null)}>
        {editing && (
          <CellEditor
            cell={editing}
            coverage={coverageByKey.get(coverageKey(editing.personaId, editing.moduleId))}
            onClose={() => setEditing(null)}
          />
        )}
      </Dialog>

      <Dialog open={editingPersona !== null} onOpenChange={(open) => !open && setEditingPersona(null)}>
        {editingPersona && (
          <PersonaEditor personaId={editingPersona} onClose={() => setEditingPersona(null)} />
        )}
      </Dialog>

      <Dialog open={creatingPersona} onOpenChange={setCreatingPersona}>
        {creatingPersona && (
          <CreatePersonaDialog
            onClose={(personaId) => {
              setCreatingPersona(false);
              // Straight into the plan editor: a persona with no plan
              // administers nothing, so creation is half of one action.
              if (personaId) setEditingPersona(personaId);
            }}
          />
        )}
      </Dialog>
    </div>
  );
}
